import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "@/lib/utils";

const Popover = PopoverPrimitive.Root;
const PopoverTrigger = PopoverPrimitive.Trigger;
/** Positions the content against an element that isn't the trigger — for a
 *  popover opened some other way (on hover, say) whose anchor can't be a
 *  button of its own. */
const PopoverAnchor = PopoverPrimitive.Anchor;

/**
 * When a Popover opens inside a Radix Dialog (our Sheet), `react-remove-scroll`
 * (a transitive dep of `react-dialog`) registers a non-passive bubble-phase
 * `wheel` listener on `document` that calls `preventDefault()` whenever the
 * wheel target is outside the dialog's lock element. The portalled popover
 * content lives outside that lock, so the browser's default scroll never
 * runs on the popover's inner scroll containers (e.g. cmdk's CommandList).
 *
 * Workaround: register a *capture-phase* wheel listener directly on the
 * popover content. Capture-phase listeners on the popover element fire
 * BEFORE any bubble-phase listener on `document`. We:
 *   1. Find the nearest scrollable ancestor of the wheel target *inside* the
 *      popover.
 *   2. Manually update its `scrollTop` by `event.deltaY`.
 *   3. `preventDefault()` (we've handled the scroll) and
 *      `stopImmediatePropagation()` (so react-remove-scroll never sees it).
 *
 * Using a *callback ref* (not `useRef + useEffect`) so the listener is
 * attached the moment Radix mounts the portalled content. With a deps-`[]`
 * `useEffect`, the ref isn't yet set on the first run.
 */
function attachWheelPassthrough(node: HTMLDivElement) {
  const onWheel = (e: WheelEvent) => {
    let cur: Element | null = e.target as Element | null;
    while (cur && node.contains(cur)) {
      if (cur instanceof HTMLElement) {
        const cs = window.getComputedStyle(cur);
        const scrollable =
          (cs.overflowY === "auto" || cs.overflowY === "scroll") &&
          cur.scrollHeight > cur.clientHeight;
        if (scrollable) {
          cur.scrollTop += e.deltaY;
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }
      }
      cur = cur.parentElement;
    }
  };
  node.addEventListener("wheel", onWheel, { capture: true, passive: false });
}

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "start", sideOffset = 6, ...props }, ref) => {
  const wheelRef = React.useCallback((node: HTMLDivElement | null) => {
    if (node && !(node as HTMLDivElement & { __wheelInstalled?: boolean }).__wheelInstalled) {
      attachWheelPassthrough(node);
      (node as HTMLDivElement & { __wheelInstalled?: boolean }).__wheelInstalled = true;
    }
  }, []);
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={(node) => {
          wheelRef(node);
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;
        }}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "z-50 rounded-lg border border-border bg-card p-3 text-foreground shadow-raised outline-none",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
});
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverAnchor, PopoverTrigger, PopoverContent };
